import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import katex from 'katex';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { parseLatexStringArray, serializeLatexStringArray } from './latex-string-array';

/* TODO: Rewrite this file to remove the need for the following eslint disables */
/* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
/* eslint-disable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change */

function LatexPreview({ latex }: { latex: string }) {
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        if (latex.trim() === '') {
            element.textContent = '';
            element.classList.remove('text-red-600', 'dark:text-red-400');
            return;
        }

        try {
            katex.render(latex, element, {
                displayMode: false,
                throwOnError: true,
            });
            element.classList.remove('text-red-600', 'dark:text-red-400');
        } catch {
            element.textContent = latex;
            element.classList.add('text-red-600', 'dark:text-red-400');
        }
    }, [latex]);

    return (
        <span
            ref={ref}
            className="inline-flex min-h-6 min-w-8 items-center rounded border border-dashed border-gray-300 bg-gray-50 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900"
            aria-label="LaTeX preview"
        />
    );
}

function moveItem<T>(items: T[], from: number, to: number): T[] {
    if (to < 0 || to >= items.length || from === to) {
        return items;
    }
    const next = [...items];
    const [removed] = next.splice(from, 1);
    next.splice(to, 0, removed);
    return next;
}

export type LatexStringArrayEditorModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: string;
    value: string;
    onApply: (value: string) => void;
};

export function LatexStringArrayEditorModal({ open, onOpenChange, title, description, value, onApply }: LatexStringArrayEditorModalProps) {
    const listId = useId();
    const [items, setItems] = useState<string[]>(['']);
    const [parseError, setParseError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }

        const parsed = parseLatexStringArray(value);
        if (parsed.ok) {
            setParseError(null); // eslint-disable-line react-hooks/set-state-in-effect -- TODO: Rewrite this so we don't need to disable this rule
            setItems(parsed.items.length > 0 ? parsed.items : ['']);
            return;
        }

        setParseError(parsed.error);
        setItems(['']);
    }, [open, value]);

    const serializedPreview = serializeLatexStringArray(items);

    const handleApply = () => {
        onApply(serializedPreview);
        onOpenChange(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={onOpenChange}
        >
            <DialogContent className="max-w-2xl sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {description ? <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p> : null}
                    {parseError ? (
                        <p
                            className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
                            role="status"
                        >
                            Could not read the current value ({parseError}). Add variables below; Apply will replace the field.
                        </p>
                    ) : null}
                    <ul
                        id={listId}
                        className="max-h-64 space-y-2 overflow-y-auto pr-1"
                    >
                        {items.map((item, index) => (
                            <li
                                key={index}
                                className="flex flex-col gap-2 rounded border border-gray-200 p-2 dark:border-gray-700"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-5 shrink-0 text-xs text-gray-500 dark:text-gray-400">{index + 1}.</span>
                                    <input
                                        type="text"
                                        value={item}
                                        aria-label={`Variable ${index + 1} LaTeX`}
                                        onChange={e => {
                                            const next = [...items];
                                            next[index] = e.target.value;
                                            setItems(next);
                                        }}
                                        className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 py-1 font-mono text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                                        placeholder="e.g. \Delta l"
                                    />
                                    <div className="flex shrink-0 gap-0.5">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            disabled={index === 0}
                                            aria-label={`Move variable ${index + 1} up`}
                                            onClick={() => setItems(moveItem(items, index, index - 1))}
                                        >
                                            <ArrowUp className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8"
                                            disabled={index === items.length - 1}
                                            aria-label={`Move variable ${index + 1} down`}
                                            onClick={() => setItems(moveItem(items, index, index + 1))}
                                        >
                                            <ArrowDown className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-red-600 hover:text-red-700 dark:text-red-400"
                                            disabled={items.length === 1 && item === ''}
                                            aria-label={`Remove variable ${index + 1}`}
                                            onClick={() => {
                                                const next = items.filter((_, i) => i !== index);
                                                setItems(next.length > 0 ? next : ['']);
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 pl-7">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Preview</span>
                                    <LatexPreview latex={item} />
                                </div>
                            </li>
                        ))}
                    </ul>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setItems([...items, ''])}
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        Add variable
                    </Button>
                    <div className="space-y-1">
                        <label className="text-xs text-gray-700 dark:text-gray-300">Generated JSON</label>
                        <pre className="overflow-x-auto rounded border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-xs dark:border-gray-700 dark:bg-gray-900">
                            {serializedPreview || '[]'}
                        </pre>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleApply}>Apply</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
